import React, {
  KeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { CloseIcon } from 'static';

import {
  StyledChip,
  StyledChipLabel,
  StyledChipRemove,
  StyledChips,
  StyledFieldRow,
  StyledMoreChip,
  StyledOption,
  StyledOptionName,
  StyledReplyEmpty,
  StyledReplySearch,
  StyledResponseFilter,
  StyledResponseLabel,
  StyledSuggestions,
} from './responseFilterStyled';

export interface ResponseOption {
  value: string;
  label: string;
}

interface ResponseFilterProps {
  options: ResponseOption[];
  value: ResponseOption[];
  onChange: (value: ResponseOption[]) => void;
}

const COLLAPSED_CHIP_LIMIT = 8;
const MAX_SUGGESTIONS = 50;
const INPUT_ID = 'email-response-filter';

const ResponseFilter = ({ options, value, onChange }: ResponseFilterProps) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const selected = value || [];

  const suggestions = useMemo(() => {
    const selectedIds = new Set(selected.map((item) => item.value));
    const normalizedQuery = query.trim().toLowerCase();

    return (options || [])
      .filter((option) => !selectedIds.has(option.value))
      .filter((option) =>
        normalizedQuery
          ? (option.label || '').toLowerCase().includes(normalizedQuery)
          : true,
      )
      .slice(0, MAX_SUGGESTIONS);
  }, [options, query, selected]);

  useEffect(() => {
    setHighlighted(0);
  }, [query, suggestions.length]);

  useEffect(() => {
    listRef.current
      ?.querySelector<HTMLElement>(`[data-index="${highlighted}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [highlighted]);

  const addOption = (option: ResponseOption) => {
    onChange([...selected, option]);
    setQuery('');
    setIsOpen(true);
    inputRef.current?.focus();
  };

  const removeOption = (option: ResponseOption) => {
    onChange(selected.filter((item) => item.value !== option.value));
    inputRef.current?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setIsOpen(true);
        setHighlighted((index) =>
          Math.min(index + 1, Math.max(suggestions.length - 1, 0)),
        );
        break;
      case 'ArrowUp':
        event.preventDefault();
        setHighlighted((index) => Math.max(index - 1, 0));
        break;
      case 'Enter':
        event.preventDefault();
        if (isOpen && suggestions[highlighted])
          addOption(suggestions[highlighted]);
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      case 'Backspace':
        if (!query && selected.length)
          removeOption(selected[selected.length - 1]);
        break;
      default:
        break;
    }
  };

  const visibleChips =
    expanded || selected.length <= COLLAPSED_CHIP_LIMIT
      ? selected
      : selected.slice(0, COLLAPSED_CHIP_LIMIT);
  const hiddenCount = selected.length - visibleChips.length;
  const showSuggestions = isOpen && (suggestions.length > 0 || !!query.trim());
  const listboxId = `${INPUT_ID}-suggestions`;

  return (
    <StyledResponseFilter>
      <StyledResponseLabel htmlFor={INPUT_ID}>
        {t('communitySettings.notifyByReplies')}
      </StyledResponseLabel>
      <StyledFieldRow
        onMouseDown={(event) => {
          const target = event.target as HTMLElement;
          if (!target.closest('button, input, [data-chip]')) {
            event.preventDefault();
            inputRef.current?.focus();
            setIsOpen(true);
          }
        }}
      >
        <StyledChips expanded={expanded && hiddenCount === 0}>
          {visibleChips.map((option) => (
            <StyledChip key={option.value} data-chip>
              <StyledChipLabel>{option.label}</StyledChipLabel>
              <StyledChipRemove
                type="button"
                aria-label={t('email.removeRecipient', { name: option.label })}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => removeOption(option)}
              >
                <CloseIcon />
              </StyledChipRemove>
            </StyledChip>
          ))}
          {hiddenCount > 0 && (
            <StyledMoreChip type="button" onClick={() => setExpanded(true)}>
              {t('email.moreRecipients', { count: hiddenCount })}
            </StyledMoreChip>
          )}
          {expanded && selected.length > COLLAPSED_CHIP_LIMIT && (
            <StyledMoreChip type="button" onClick={() => setExpanded(false)}>
              {t('email.showLess')}
            </StyledMoreChip>
          )}
          <StyledReplySearch
            id={INPUT_ID}
            ref={inputRef}
            value={query}
            autoComplete="off"
            role="combobox"
            aria-expanded={showSuggestions}
            aria-controls={listboxId}
            aria-autocomplete="list"
            aria-activedescendant={
              showSuggestions ? `${listboxId}-${highlighted}` : undefined
            }
            placeholder={selected.length ? '' : t('common.search')}
            onChange={(event) => {
              setQuery(event.target.value);
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            onClick={() => setIsOpen(true)}
            onBlur={() => setIsOpen(false)}
            onKeyDown={handleKeyDown}
          />
        </StyledChips>
      </StyledFieldRow>
      {showSuggestions && (
        <StyledSuggestions id={listboxId} role="listbox" ref={listRef}>
          {suggestions.length === 0 && (
            <StyledReplyEmpty>{t('common.noResultsFound')}</StyledReplyEmpty>
          )}
          {suggestions.map((option, index) => (
            <StyledOption
              key={option.value}
              id={`${listboxId}-${index}`}
              data-index={index}
              role="option"
              aria-selected={index === highlighted}
              highlighted={index === highlighted}
              onMouseDown={(event) => event.preventDefault()}
              onMouseEnter={() => setHighlighted(index)}
              onClick={() => addOption(option)}
            >
              <StyledOptionName>{option.label}</StyledOptionName>
            </StyledOption>
          ))}
        </StyledSuggestions>
      )}
    </StyledResponseFilter>
  );
};

export default ResponseFilter;
